import { useEffect, useMemo } from 'react';
import { IntlShape } from 'react-intl';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import useRefreshEhrData from '../../PatientDetail/Header/EhrRefreshButton/useRefreshEhrData';
import { patientPropsSelector } from '../../../../redux/selectors/patientSelector';
import { wasMemberIngested } from './utils';
import {
  getElationPatientChartUrl,
  getElationPatientId,
} from '../../../../util/elation';
import { memberDetailMessages } from './messages';
import FhirDateTime from '@lifeomic/phc-web-toolkit/src/util/fhirResourceParsers/FhirDateTime';

const fhirService = new FhirService('Patient');

const dateTimeParser = new FhirDateTime();

const formatBirthDate = (birthDate?: string): string => {
  if (!birthDate) {
    return '';
  }
  const formatted = dateTimeParser.getDisplayValue(birthDate);
  return formatted ?? birthDate;
};

type UseMemberDetailDataParams = {
  accountId?: string;
  memberId?: string;
  isMasked: boolean;
  mrnConfig?: unknown;
  formatMessage: IntlShape['formatMessage'];
};

export const useMemberDetailData = ({
  accountId,
  memberId,
  isMasked,
  mrnConfig,
  formatMessage,
}: UseMemberDetailDataParams) => {
  const [fetchMember, fetchMemberState] = useService(fhirService.getById);

  useEffect(() => {
    if (accountId && memberId) {
      fetchMember(accountId, memberId);
    }
  }, [accountId, memberId, fetchMember]);

  const member = fetchMemberState.response?.data || null;
  const memberProps = member
    ? patientPropsSelector(member, isMasked, mrnConfig)
    : null;
  const memberDisplayName = useMemo(() => {
    const memberFirstName = memberProps?.name?.given || '';
    const memberLastName = memberProps?.name?.family || '';
    const memberName = [memberFirstName, memberLastName]
      .filter(Boolean)
      .join(' ');
    return (
      memberName ||
      memberProps?.name?.display ||
      formatMessage(memberDetailMessages.unknownMember)
    );
  }, [formatMessage, memberProps]);
  const memberBirthDate = memberProps?.birthDate || member?.birthDate || '';
  const formattedBirthDate = formatBirthDate(memberBirthDate);
  const elationChartUrl = getElationPatientChartUrl(
    getElationPatientId(member),
  );
  const isLoading = fetchMemberState.isFetching && !member;
  const canSync = !!member && wasMemberIngested(member);
  const refreshEhrData = useRefreshEhrData(member as fhir.Patient);

  return {
    member,
    memberProps,
    memberDisplayName,
    formattedBirthDate,
    elationChartUrl,
    isLoading,
    canSync,
    refreshEhrData,
  };
};
